import React, { useState } from 'react';

import PropTypes from 'prop-types';
import { Message, Input, Search, List, ListItem, ListContent, Button } from 'semantic-ui-react';

import { isNil } from 'lodash';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import { useSelector } from 'react-redux';

import { getErrorMessage } from 'libs/errors';

import {
  INVALID_EMAIL_ERROR_MESSAGE,
  DEFAULT_DESTINATION_EMAIL_MESSAGE,
  ERROR_GETTING_DESTINATION_EMAILS_MESSAGE,
} from './FormDestinationEmail.constants';
import {
  isValidEmail,
  isAnOption,
  newValueToOption,
  optionToEmail,
  createEmailOptions,
  destinationEmailToOption,
} from './FormDestinationEmail.utils';
import API from '../../../../../../../../../libs/api';
import { selectBusinessId } from '../../../../../../../../../selectors';
import useAsyncEffect from '../../../../../../../foundation/hooks/use-async-effect';

import './FormDestinationEmail.scss';

const propTypes = {
  selectedEmails: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func,
  locationId: PropTypes.number,
  label: PropTypes.string,
  noEmailsHelpMessage: PropTypes.string,
};
const defaultProps = {
  selectedEmails: null,
  locationId: null,
  onChange: () => null,
  label: 'Email Destinations',
  noEmailsHelpMessage: DEFAULT_DESTINATION_EMAIL_MESSAGE,
};

export default function FormDestinationEmail({
  selectedEmails: selectedEmailsProp,
  onChange,
  locationId,
  label,
  noEmailsHelpMessage,
}) {
  const businessId = useSelector(selectBusinessId);
  const [emailOptions, setEmailOptions] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [error, setError] = useState(null);

  const isControlled = !_isNil(selectedEmailsProp);

  const [selectedEmailsState, setSelectedEmails] = useState([]);

  const selectedEmails = isControlled ? selectedEmailsProp : selectedEmailsState;

  useAsyncEffect(async () => {
    try {
      setIsLoading(true);
      const destinationEmails = await API.getDestinationEmails(businessId);
      const options = createEmailOptions(destinationEmails, locationId);
      setEmailOptions(options);

      if (!isControlled) {
        if (!_isNil(locationId)) {
          setSelectedEmails(options.map(({ value: email }) => email));
        }
      }
    } catch (e) {
      setError(ERROR_GETTING_DESTINATION_EMAILS_MESSAGE);
    } finally {
      setIsLoading(false);
    }
  }, []);

  function onSearch(value) {
    setSearchValue(value);
    setIsSearching(true);
    setError(null);

    if (value.length < 1) {
      setSearchResults([]);
    } else {
      const searchOptions = emailOptions.filter(
        ({ title, location }) => title.includes(value) && isNil(location),
      );

      if (!selectedEmails.includes(value) && !isAnOption(value, emailOptions)) {
        searchOptions.push(newValueToOption(value));
      }
      setSearchResults(searchOptions);
    }

    setIsSearching(false);
  }

  async function onSelect(option) {
    try {
      setIsLoading(true);
      const email = optionToEmail(option);
      if (!isValidEmail(email)) {
        setError(INVALID_EMAIL_ERROR_MESSAGE);
        return;
      }

      if (!isAnOption(email, emailOptions)) {
        const destinationEmail = await API.addDestinationEmail(businessId, email, { locationId });

        if (!_isNil(locationId)) {
          setEmailOptions((prevEmailOptions) => [
            ...prevEmailOptions,
            destinationEmailToOption(destinationEmail),
          ]);
        }
      }

      const newSelectedEmails = selectedEmails.includes(email)
        ? selectedEmails
        : [...selectedEmails, email];

      if (isControlled) {
        onChange(newSelectedEmails);
      } else {
        setSelectedEmails(newSelectedEmails);
      }

      setSearchValue('');
      setSearchResults([]);
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setIsLoading(false);
    }
  }

  async function onDelete(emailToDelete) {
    try {
      setIsLoading(true);
      if (!isControlled) {
        if (!_isNil(locationId)) {
          const id = _get(
            emailOptions.find(({ value: email }) => email === emailToDelete),
            'id',
          );

          await API.deleteDestinationEmail(id);
          setEmailOptions(emailOptions.filter(({ value: email }) => email !== emailToDelete));
        }
        setSelectedEmails(selectedEmails.filter((email) => email !== emailToDelete));
      } else {
        onChange(selectedEmails.filter((email) => email !== emailToDelete));
      }

      setSearchResults([]);
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="email-destination-form">
      <label htmlFor="search-input">{label}</label>

      <Search
        id="search-input"
        label="Email Destination"
        input={
          <Input
            loading={isSearching}
            disabled={isLoading}
            icon="search"
            iconPosition="left"
            type="email"
            autoComplete="off"
          />
        }
        placeholder="Search for email addresses..."
        value={searchValue}
        results={searchResults}
        onSearchChange={(e, { value }) => onSearch(value)}
        onResultSelect={(e, { result }) => onSelect(result)}
        selectFirstResult
      />

      {_isEmpty(selectedEmails) && <Message info content={noEmailsHelpMessage} />}

      {error && <Message error content={error} />}

      {!_isEmpty(selectedEmails) && (
        <List divided className="email-destinations">
          {selectedEmails.map((email) => (
            <ListItem key={email}>
              <ListContent className="email">{email}</ListContent>
              <ListContent>
                <Button
                  color="red"
                  basic
                  size="mini"
                  icon="trash"
                  className="delete"
                  onClick={() => onDelete(email)}
                />
              </ListContent>
            </ListItem>
          ))}
        </List>
      )}
    </div>
  );
}

FormDestinationEmail.propTypes = propTypes;
FormDestinationEmail.defaultProps = defaultProps;
